import React, { useMemo } from 'react'

import { isAnnouncementNotification } from '@/lib/notification-navigation'

import { UserNotification as UserNotificationIcon } from '@/components/common/user-notification'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuItemNotification,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import { useNotification } from '@/contexts/notification-context'
import { formatNotificationSubjectHtml } from '@/utils/notification-subject-format'

import { UserNotificationType } from '@/types/notification.type'

type NotificationAppearance = {
  label: string
  badgeClassName: string
  containerClassName: string
  unreadDotClassName: string
}

const stripHtml = (value: string) =>
  value
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const parseSafeUrl = (value: string) => {
  try {
    return value.startsWith('http') ? new URL(value) : new URL(value, 'http://localhost')
  } catch {
    return null
  }
}

const getNotificationAppearance = (notification: UserNotificationType): NotificationAppearance => {
  const subject = stripHtml(notification.subject).toLowerCase()
  const url = parseSafeUrl(notification.destinationUrl)
  const path = url?.pathname ?? ''

  if (isAnnouncementNotification(notification)) {
    return {
      label: 'Announcement',
      badgeClassName: 'border-sky-200 bg-sky-50 text-sky-700',
      containerClassName: notification.isRead
        ? 'border-sky-100 bg-white hover:border-sky-200 hover:bg-sky-50/40'
        : 'border-sky-200 bg-sky-50/70 hover:border-sky-300 hover:bg-sky-50',
      unreadDotClassName: 'bg-sky-500',
    }
  }

  if (path.includes('/chat')) {
    return {
      label: 'Chat',
      badgeClassName: 'border-emerald-200 bg-emerald-50 text-emerald-700',
      containerClassName: notification.isRead
        ? 'border-emerald-100 bg-white hover:border-emerald-200 hover:bg-emerald-50/40'
        : 'border-emerald-200 bg-emerald-50/70 hover:border-emerald-300 hover:bg-emerald-50',
      unreadDotClassName: 'bg-emerald-500',
    }
  }

  if (path.includes('/topic') || subject.includes('thảo luận') || subject.includes('discussion')) {
    return {
      label: 'Discussion',
      badgeClassName: 'border-violet-200 bg-violet-50 text-violet-700',
      containerClassName: notification.isRead
        ? 'border-violet-100 bg-white hover:border-violet-200 hover:bg-violet-50/40'
        : 'border-violet-200 bg-violet-50/70 hover:border-violet-300 hover:bg-violet-50',
      unreadDotClassName: 'bg-violet-500',
    }
  }

  if (subject.includes('review') || path.includes('/code-review')) {
    return {
      label: 'Code Review',
      badgeClassName: 'border-amber-200 bg-amber-50 text-amber-700',
      containerClassName: notification.isRead
        ? 'border-amber-100 bg-white hover:border-amber-200 hover:bg-amber-50/40'
        : 'border-amber-200 bg-amber-50/70 hover:border-amber-300 hover:bg-amber-50',
      unreadDotClassName: 'bg-amber-500',
    }
  }

  if (subject.includes('mention') || subject.includes('@')) {
    return {
      label: 'Mention',
      badgeClassName: 'border-indigo-200 bg-indigo-50 text-indigo-700',
      containerClassName: notification.isRead
        ? 'border-indigo-100 bg-white hover:border-indigo-200 hover:bg-indigo-50/40'
        : 'border-indigo-200 bg-indigo-50/70 hover:border-indigo-300 hover:bg-indigo-50',
      unreadDotClassName: 'bg-indigo-500',
    }
  }

  if (subject.includes('reaction') || subject.includes('react') || subject.includes('thích')) {
    return {
      label: 'Reaction',
      badgeClassName: 'border-pink-200 bg-pink-50 text-pink-700',
      containerClassName: notification.isRead
        ? 'border-pink-100 bg-white hover:border-pink-200 hover:bg-pink-50/40'
        : 'border-pink-200 bg-pink-50/70 hover:border-pink-300 hover:bg-pink-50',
      unreadDotClassName: 'bg-pink-500',
    }
  }

  return {
    label: 'General',
    badgeClassName: 'border-slate-200 bg-slate-50 text-slate-700',
    containerClassName: notification.isRead
      ? 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
      : 'border-blue-100 bg-blue-50/70 hover:border-blue-200 hover:bg-blue-50',
    unreadDotClassName: 'bg-blue-500',
  }
}

interface UserNotificationNavProps {
  triggerClassName?: string
}

export function UserNotificationNav({ triggerClassName }: UserNotificationNavProps = {}) {
  const {
    notifications,
    unreadCount,
    openNotificationDetail,
    navigateToNotifications,
    formattedTimeAgo,
    tabNotificationCount,
    clearTabNotifications,
  } = useNotification()

  const renderNotificationItem = (notification: UserNotificationType) => (
    <div key={notification.id} className="w-full px-2 pb-2">
      <DropdownMenuItemNotification asChild>
        <div
          className={`group relative flex w-full cursor-pointer flex-col items-start gap-2 rounded-xl border p-3 transition-all ${getNotificationAppearance(notification).containerClassName}`}
          onClick={() => openNotificationDetail(notification)}
        >
          <div className="flex w-full items-center justify-between gap-2">
            <span
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${getNotificationAppearance(notification).badgeClassName}`}
            >
              {getNotificationAppearance(notification).label}
            </span>
            {!notification.isRead && (
              <span className={`h-2 w-2 rounded-full ${getNotificationAppearance(notification).unreadDotClassName}`} />
            )}
          </div>
          <div
            className="w-full min-w-0 break-words text-left text-sm font-medium leading-5 text-slate-800 line-clamp-2"
            dangerouslySetInnerHTML={{ __html: formatNotificationSubjectHtml(notification.subject) }}
          />
          <div className="flex w-full items-center justify-between">
            <span className="text-xs text-slate-500">{formattedTimeAgo(notification.createdAt)}</span>
          </div>
        </div>
      </DropdownMenuItemNotification>
    </div>
  )

  const renderTabNotificationBanner = () => {
    if (tabNotificationCount === 0) return null

    return (
      <>
        <div className="mx-2 my-2 rounded-xl border border-blue-100 bg-blue-50/70 p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  {tabNotificationCount} new notification{tabNotificationCount > 1 ? 's' : ''} while you were away
                </p>
              </div>
            </div>
            <button
              onClick={e => {
                e.stopPropagation()
                clearTabNotifications()
              }}
              className="text-sm font-medium text-blue-500 transition-colors hover:text-blue-700"
            >
              Clear
            </button>
          </div>
        </div>
      </>
    )
  }

  const notificationsList = useMemo(() => {
    const content = []

    content.push(renderTabNotificationBanner())

    if (notifications.length === 0) {
      content.push(
        <div key="empty" className="p-8 text-center text-sm text-slate-500">
          Không có thông báo nào
        </div>
      )
    } else {
      content.push(...notifications.map(notification => renderNotificationItem(notification)))
    }

    return content
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications, tabNotificationCount])

  const viewMoreButton = useMemo(() => {
    if (notifications.length >= 5) {
      return (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <div
              className="mx-2 mb-2 flex cursor-pointer items-center justify-center rounded-lg border border-slate-200 p-3 transition-colors hover:bg-slate-50"
              onClick={navigateToNotifications}
            >
              <span className="text-sm font-medium text-blue-600">Xem tất cả thông báo</span>
            </div>
          </DropdownMenuItem>
        </>
      )
    }
    return null
  }, [notifications.length, navigateToNotifications])

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="cursor-pointer">
          <UserNotificationIcon unreadNotifications={unreadCount} className={triggerClassName} />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={12}
        className="z-[10050] w-screen rounded-2xl border-slate-200 bg-white p-0 shadow-xl sm:w-[420px]"
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <h3 className="text-base font-semibold text-slate-900">Thông báo</h3>
          {unreadCount > 0 && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
              {unreadCount} chưa đọc
            </span>
          )}
        </div>
        <div className="max-h-[420px] w-full overflow-y-auto py-2">{notificationsList}</div>
        {viewMoreButton}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
